"use client";

import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/ui/reveal";
import { useEarlyAccess } from "@/components/early-access";
import { ModelLogo } from "@/components/dashboard/model-logos";
import {
  DEPLOYMENT_LABEL,
  FEATURED_MODELS,
  formatModelPrice,
  KIND_LABEL,
  type CatalogModel,
} from "@/lib/dashboard/models";
import { LinkButton } from "@/components/ui/buttons";
import { ease, fadeUp, stagger } from "@/lib/motion";
import { cn } from "@/lib/utils";

/**
 * The models band: five cards from the same catalogue the dashboard renders,
 * so the marketing site cannot list a model the platform does not. It replaced
 * two marquee rows of fourteen model names on 2026-08-25: those named weights
 * the gateway had never served, and a scrolling logo cloud answers "who do you
 * know" rather than "what can I call". Five cards answer the second question,
 * with the price on the front.
 *
 Not the whole catalogue: since 2026-10-08 it is every **live** route, one
 * card per model (five on `main` that day), derived in `FEATURED_MODELS`.
 * Coming-soon routes stay on the `/models` page this band links to; at
 * sixteen rows the full list would be an inventory, not a pitch.
 *
 * Opening a card (the order the team asked for on 2026-08-26): a click, a tap
 * or Enter lifts the card, front side up, from its slot to the centre of the
 * screen at reading size while the page dims behind it; once it has arrived
 * it turns over to its model-card side. Closing runs the same film backwards:
 * the card turns back to its front, then travels back to its slot. A click on
 * the card, on the dimmed page or on Escape closes it. Hover does not flip: a
 * card that turns over as the pointer passes and then has to be clicked as
 * well is two gestures for one intent, so hover lifts it a little and says
 * what a click does.
 *
 * Mechanics: the slot's rectangle is measured on click and a fixed copy of
 * the card is animated from that rectangle to the centre one (real top, left,
 * width and height, so the content reflows rather than being scaled and
 * snapped back, which a transform-based shared-layout move did). The slot
 * keeps a dashed placeholder the same size, so the grid never reflows. The
 * flip is a rotateY transition on a `preserve-3d` wrapper inside the moving
 * box; it starts when the move completes, and the return starts when the
 * flip back completes.
 */

/**
 * Columns at `xl`, chosen from how many live routes the band shows so the last
 * row is never a lone card (2026-10-16: six live routes left one card on a row
 * of its own). Five or fewer sit in one row; a multiple of three fills rows of
 * three; anything else falls back to four. Literal class names, so Tailwind
 * keeps them.
 */
const WIDE_COLUMNS =
  FEATURED_MODELS.length <= 5
    ? (["xl:grid-cols-1", "xl:grid-cols-1", "xl:grid-cols-2", "xl:grid-cols-3", "xl:grid-cols-4", "xl:grid-cols-5"] as const)[FEATURED_MODELS.length]
    : FEATURED_MODELS.length % 3 === 0
      ? "xl:grid-cols-3"
      : "xl:grid-cols-4";

const FLIP_MS = 560;
const MOVE_S = 0.62;

type Rect = { top: number; left: number; width: number; height: number };
type Phase = "lifting" | "open" | "closing" | "returning";
type Stage = { id: string; from: Rect; to: Rect; phase: Phase };

/** True on devices whose primary input can hover; false on touch screens. */
function useCanHover(): boolean {
  const [can, setCan] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.("(hover: hover) and (pointer: fine)");
    if (!mq) return;
    setCan(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setCan(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return can;
}

function measure(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/** Reading size, centred in the viewport. */
function centreRect(): Rect {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(vw * 0.92, 640);
  const height = Math.min(vh * 0.9, 576);
  return { top: (vh - height) / 2, left: (vw - width) / 2, width, height };
}

/**
 * One line under the name: what the weights are, without a number.
 *
 * Three rungs, most specific first. A full pedigree gives the lab and the
 * parameter count; a row with no pedigree still usually knows whose weights
 * these are; and only a row that knows neither falls back to naming the kind
 * of route, which tells a reader nothing they cannot see from the price
 * labels. The rungs matter since 2026-08-30, when eleven rows arrived with a
 * `lab` and no pedigree.
 */
function tagline(m: CatalogModel): string {
  if (m.pedigree) return `${m.pedigree.lab} · ${m.pedigree.parameters}`;
  return m.lab ?? KIND_LABEL[m.kind].route;
}

/**
 * "Route id", or the same words with the warning a proposed id needs.
 *
 * The label is where the qualifier goes rather than the code block: the string
 * itself has to stay exactly copyable, and a "(provisional)" appended to the
 * id would end up pasted into somebody's `model` field.
 */
function idLabel(m: CatalogModel): string {
  return m.idProvisional ? "Route id (proposed, not live yet)" : "Route id";
}

function PoolTag({ model }: { model: CatalogModel }) {
  const soon = model.status === "coming-soon";
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
      <span className="rounded-full border border-border px-2 py-0.5 font-sans text-[0.62rem] uppercase tracking-[0.14em] text-muted">
        {DEPLOYMENT_LABEL[model.deployment]}
      </span>
      {soon && (
        <span className="rounded-full bg-foreground/[0.07] px-2 py-0.5 font-sans text-[0.62rem] uppercase tracking-[0.14em] text-foreground">
          Coming soon
        </span>
      )}
    </span>
  );
}

function Price({ label, value }: { label: string; value: number | "none" | null }) {
  const { text, muted } = formatModelPrice(value);
  return (
    <div>
      <p className="font-sans text-[0.68rem] uppercase tracking-[0.14em] text-muted-2">{label}</p>
      <p className={cn("mt-0.5 font-sans text-sm tabular-nums", muted ? "text-muted" : "text-foreground")}>
        {text}
      </p>
    </div>
  );
}

const FACE =
  "absolute inset-0 flex flex-col rounded-lg border border-border bg-card [backface-visibility:hidden] [-webkit-backface-visibility:hidden]";

const STAGE_SHADOW = "shadow-[0_48px_120px_-40px_rgba(0,0,0,0.55)]";

const DASH_LINK =
  "inline-flex items-center gap-1.5 font-sans font-medium text-foreground underline decoration-border-strong underline-offset-4 transition-colors hover:decoration-foreground";

interface CardProps {
  model: CatalogModel;
  flipped: boolean;
  /** Reading size, in the centre of the screen. */
  stage: boolean;
  canHover: boolean;
  onActivate: () => void;
}

function ModelCard({ model, flipped, stage, canHover, onActivate }: CardProps) {
  const ped = model.pedigree;
  // "Full model card in the dashboard" linked to /dashboard/models until
  // 2026-08-30. Signed out, that is a redirect to the login page, which is a
  // dead end while the product is gated behind early access, so both copies of
  // the link now open the modal instead. It stays a text link in look and in
  // label; only the destination changed. `stopPropagation` is still needed
  // either way: the whole card is a click target that flips it.
  const { open: openEarlyAccess } = useEarlyAccess();

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate();
    }
  };

  return (
    <div className={cn("relative h-full w-full [perspective:1600px]", !stage && "group min-h-[23rem]")}>
      <div
        role={stage ? "dialog" : "button"}
        aria-modal={stage || undefined}
        aria-haspopup={stage ? undefined : "dialog"}
        tabIndex={0}
        data-stage={stage ? "true" : undefined}
        data-model-card={model.id}
        aria-label={
          stage
            ? `${model.name} model card. Activate to close.`
            : `${model.name}: open the model card`
        }
        onClick={onActivate}
        onKeyDown={onKey}
        className={cn(
          "relative h-full w-full cursor-pointer rounded-lg outline-none transition-transform duration-[560ms] ease-[cubic-bezier(0.22,1,0.36,1)] [transform-style:preserve-3d] motion-reduce:transition-none",
          !stage && "group-hover:-translate-y-1",
        )}
        style={{ transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)" }}
      >
        {/* front: the same face at both sizes, so it never reflows mid-flight */}
        <div
          className={cn(
            FACE,
            "p-5",
            stage ? STAGE_SHADOW : "transition-shadow group-hover:shadow-[0_18px_44px_-24px_rgba(0,0,0,0.32)]",
          )}
          aria-hidden={flipped}
          // `inert` and not `aria-hidden` alone. A hidden face still holds a
          // real <button>, and aria-hidden removes it from the accessibility
          // tree without removing it from the tab order, so a keyboard user
          // lands on a control they cannot see. `inert` does both, and React
          // 19 passes it through as a boolean.
          inert={flipped}
        >
          <div className="flex items-start justify-between gap-3">
            <ModelLogo logo={model.logo} size={30} />
            <PoolTag model={model} />
          </div>
          <h3 className="mt-5 font-serif text-2xl font-light leading-tight tracking-tight">{model.name}</h3>
          <p className="mt-1.5 font-sans text-sm text-muted">{tagline(model)}</p>
          <p className="mt-1 font-sans text-xs text-muted-2">
            {KIND_LABEL[model.kind].long}
            {ped ? ` · ${ped.contextWindow} context` : ""}
          </p>

          <div className="mt-auto grid grid-cols-2 gap-3 border-t border-border pt-4">
            <Price label="Input / 1M" value={model.inputPkrPerMillion} />
            <Price label="Output / 1M" value={model.outputPkrPerMillion} />
          </div>
          <p className={cn("mt-3 font-sans text-[0.68rem] text-muted-2", stage && "invisible")}>
            {canHover ? "Click to open the model card" : "Tap to open the model card"}
          </p>
        </div>

        {/* back: the model card */}
        <div
          className={cn(FACE, "[transform:rotateY(180deg)]", stage ? cn("overflow-y-auto p-7 sm:p-8", STAGE_SHADOW) : "p-5")}
          aria-hidden={!flipped}
          inert={!flipped}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              {stage && <ModelLogo logo={model.logo} size={28} />}
              <p className={cn("font-medium text-foreground", stage ? "font-serif text-2xl font-light" : "font-sans text-sm")}>
                {model.name}
              </p>
            </div>
            {stage ? <PoolTag model={model} /> : <ModelLogo logo={model.logo} size={20} />}
          </div>

          {ped ? (
            stage ? (
              <div className="mt-6 grid gap-7 sm:grid-cols-[1.1fr_1fr] sm:gap-8">
                <div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                    {[
                      ["Lab", ped.lab],
                      ["Parameters", ped.parameters],
                      ["Licence", ped.license],
                      ["Released", ped.released],
                      ["Context", ped.contextWindow],
                      ["Kind", KIND_LABEL[model.kind].long],
                    ].map(([k, v]) => (
                      <div key={k} className="min-w-0">
                        <dt className="font-sans text-[0.62rem] uppercase tracking-[0.14em] text-muted-2">{k}</dt>
                        <dd className="mt-0.5 font-sans text-sm leading-snug text-foreground">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="mt-6 font-sans text-sm leading-relaxed text-muted">{ped.summary}</p>
                </div>
                <div>
                  <p className="font-sans text-[0.62rem] uppercase tracking-[0.14em] text-muted-2">Good for</p>
                  <ul className="mt-2 flex flex-col gap-2.5">
                    {ped.goodFor.map((g) => (
                      <li key={g} className="flex items-start gap-2.5 font-sans text-sm leading-relaxed text-foreground/85">
                        <span aria-hidden="true" className="mt-[0.55rem] h-1 w-1 shrink-0 rounded-full bg-muted-2" />
                        <span>{g}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <>
                <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2.5">
                  {[
                    ["Lab", ped.lab],
                    ["Parameters", ped.parameters],
                    ["Licence", ped.license],
                    ["Released", ped.released],
                  ].map(([k, v]) => (
                    <div key={k} className="min-w-0">
                      <dt className="font-sans text-[0.62rem] uppercase tracking-[0.14em] text-muted-2">{k}</dt>
                      <dd className="mt-0.5 font-sans text-xs leading-snug text-foreground">{v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-4 font-sans text-[0.62rem] uppercase tracking-[0.14em] text-muted-2">Good for</p>
                <ul className="mt-1.5 flex flex-col gap-1.5">
                  {ped.goodFor.slice(0, 2).map((g) => (
                    <li key={g} className="flex items-start gap-2 font-sans text-xs leading-snug text-muted">
                      <span aria-hidden="true" className="mt-[0.45rem] h-1 w-1 shrink-0 rounded-full bg-muted-2" />
                      <span>{g.split(":")[0]}</span>
                    </li>
                  ))}
                </ul>
              </>
            )
          ) : (
            <div className={cn("font-sans leading-relaxed text-muted", stage ? "mt-6 max-w-md text-sm" : "mt-4 text-xs")}>
              {model.blurb && <p className="text-foreground/85">{model.blurb}</p>}
              {model.lab && (
                <p className={cn(model.blurb && "mt-3")}>
                  Weights by {model.lab}.
                </p>
              )}
              <p className={cn((model.blurb || model.lab) && "mt-3")}>
                No published model card for these weights yet. The route id and price are on the
                front.
              </p>
            </div>
          )}

          {stage ? (
            <div className="mt-auto border-t border-border pt-5">
              <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
                <div className="min-w-0">
                  <p className="font-sans text-[0.62rem] uppercase tracking-[0.14em] text-muted-2">
                    {idLabel(model)}
                  </p>
                  <code className="mt-1 block break-all font-mono text-xs leading-snug text-foreground">{model.id}</code>
                </div>
                <div className="flex gap-8">
                  <Price label="Input / 1M" value={model.inputPkrPerMillion} />
                  <Price label="Output / 1M" value={model.outputPkrPerMillion} />
                </div>
              </div>
              <div className="mt-5 flex flex-wrap items-center justify-between gap-x-5 gap-y-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEarlyAccess();
                  }}
                  className={cn(DASH_LINK, "text-sm")}
                >
                  Full model card in the dashboard
                  <span aria-hidden="true">→</span>
                </button>
                <span className="font-sans text-xs text-muted-2">Click the card or press Escape to close</span>
              </div>
            </div>
          ) : (
            <div className="mt-auto border-t border-border pt-3">
              <p className="font-sans text-[0.62rem] uppercase tracking-[0.14em] text-muted-2">
                {idLabel(model)}
              </p>
              <code className="mt-0.5 block break-all font-mono text-[0.66rem] leading-snug text-foreground">
                {model.id}
              </code>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openEarlyAccess();
                }}
                className={cn(DASH_LINK, "mt-3 text-xs")}
              >
                Full model card in the dashboard
                <span aria-hidden="true">→</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Models() {
  const canHover = useCanHover();
  const reduced = useReducedMotion() ?? false;
  const [stage, setStage] = useState<Stage | null>(null);
  const timer = useRef<number | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  const clearTimer = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
  };

  const open = useCallback((id: string, el: Element) => {
    clearTimer();
    returnFocus.current = document.activeElement as HTMLElement | null;
    setStage({ id, from: measure(el), to: centreRect(), phase: "lifting" });
  }, []);

  /** The move has arrived at the centre: turn the card over. */
  const arrived = useCallback(() => {
    setStage((s) => (s && s.phase === "lifting" ? { ...s, phase: "open" } : s));
  }, []);

  /** Turn the card back to its front, then send it home. */
  const close = useCallback(() => {
    setStage((s) => {
      if (!s || s.phase !== "open") return s;
      clearTimer();
      timer.current = window.setTimeout(
        () => {
          setStage((cur) => {
            if (!cur || cur.phase !== "closing") return cur;
            // the slot may have moved (a resize); go to where it is now
            const slot = document.querySelector(`[data-model-slot="${CSS.escape(cur.id)}"]`);
            return { ...cur, phase: "returning", from: slot ? measure(slot) : cur.from };
          });
        },
        reduced ? 0 : FLIP_MS,
      );
      return { ...s, phase: "closing" };
    });
  }, [reduced]);

  /** Back in the slot: hand the slot its card again. */
  const returned = useCallback(() => {
    setStage((s) => (s && s.phase === "returning" ? null : s));
    returnFocus.current?.focus?.();
  }, []);

  useEffect(() => () => clearTimer(), []);

  // While a card is out of its slot: Escape closes, the page does not scroll,
  // and the open card takes focus once it has turned over.
  useEffect(() => {
    if (!stage) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [stage !== null, close]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (stage?.phase !== "open") return;
    const focus = window.setTimeout(() => {
      document.querySelector<HTMLElement>('[data-stage="true"]')?.focus();
    }, 50);
    return () => window.clearTimeout(focus);
  }, [stage?.phase]);

  // Only a featured card can be lifted, because only a featured card is on
  // this page to click. Looking the id up in the full catalogue would let a
  // stale stage id from a previous render open a card with no slot to fly
  // from and no slot to return to.
  const lifted = stage ? FEATURED_MODELS.find((m) => m.id === stage.id) ?? null : null;
  const moveTo = stage?.phase === "returning" ? stage.from : stage?.to;

  return (
    <section id="models" className="relative scroll-mt-24 py-14 sm:py-24">
      <div className="mx-auto max-w-[90rem] px-5 sm:px-8">
        <Reveal className="mb-12 text-center">
          <p className="eyebrow">Powered by optimised open-source AI</p>
          <h2 className="mt-4 font-serif text-2xl font-light tracking-tight sm:text-3xl">
            One API. Every leading open-source AI model.
          </h2>
          <p className="mx-auto mt-4 max-w-xl font-sans text-sm leading-relaxed text-muted">
            Every route P/9 serves today, priced per million tokens in rupees. Every card is the
            same catalogue the dashboard bills against.
          </p>
        </Reveal>

        <motion.div
          variants={stagger(0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className={cn("grid gap-4 sm:grid-cols-2 lg:grid-cols-3", WIDE_COLUMNS)}
        >
          {FEATURED_MODELS.map((m) => {
            const away = stage?.id === m.id;
            return (
              <motion.div key={m.id} variants={fadeUp} className="min-h-[23rem]" data-model-slot={m.id}>
                {away ? (
                  // the slot the card left: keeps the grid from reflowing
                  <div aria-hidden="true" className="h-full min-h-[23rem] rounded-lg border border-dashed border-border" />
                ) : (
                  <ModelCard
                    model={m}
                    flipped={false}
                    stage={false}
                    canHover={canHover}
                    onActivate={() => {
                      const slot = document.querySelector(`[data-model-slot="${CSS.escape(m.id)}"]`);
                      if (slot && !stage) open(m.id, slot);
                    }}
                  />
                )}
              </motion.div>
            );
          })}
        </motion.div>

        {/* The way out of a curated band: the whole catalogue, priced, on its
            own page. A secondary skin because the section's own call to action
            is further down the page; this is navigation, not the pitch. */}
        <Reveal className="mt-10 flex justify-center sm:mt-12" amount={0.4}>
          <LinkButton href="/models" variant="secondary">
            View all models
          </LinkButton>
        </Reveal>
      </div>

      {lifted && stage && moveTo && (
        <div className="fixed inset-0 z-[80]" data-model-stage data-phase={stage.phase}>
          <motion.button
            type="button"
            aria-label="Close the model card"
            initial={{ opacity: 0 }}
            animate={{ opacity: stage.phase === "returning" ? 0 : 1 }}
            transition={{ duration: reduced ? 0 : 0.45, ease }}
            onClick={close}
            className="absolute inset-0 cursor-default bg-background/75 backdrop-blur-sm"
          />
          <motion.div
            className="absolute"
            initial={{ top: stage.from.top, left: stage.from.left, width: stage.from.width, height: stage.from.height }}
            animate={{ top: moveTo.top, left: moveTo.left, width: moveTo.width, height: moveTo.height }}
            transition={{ duration: reduced ? 0 : MOVE_S, ease }}
            onAnimationComplete={stage.phase === "lifting" ? arrived : stage.phase === "returning" ? returned : undefined}
          >
            <ModelCard
              model={lifted}
              flipped={stage.phase === "open"}
              stage
              canHover={canHover}
              onActivate={close}
            />
          </motion.div>
        </div>
      )}
    </section>
  );
}
